import React from "react";
import Colors from '../../../../themes/Colors';
import {
  Icon,
  Row,
  Col,
  Select,
  Button,
  Checkbox,
  Input,
  Slider,
  Switch,
  InputNumber,
  Tooltip,
} from "antd";

const { Option } = Select;

// Original refine-filters layout, used inside the map view's modal.
// Purely presentational: all state and handlers live in RefinateFiltersForm.
const styles = {
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: Colors.black,
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
  },
  label: {
    fontSize: "12px",
    color: Colors.textStrong,
    marginBottom: "4px",
    display: "block",
    fontWeight: 400,
  },
  inputRadius: {
    borderRadius: "4px",
  },
  suffixText: {
    color: Colors.textMuted,
    fontSize: "12px",
  },
  mainGutter: 40,
  rowGap: 24,
  sliderTrack: { backgroundColor: "#1890ff" },
  sliderHandle: { borderColor: "#1890ff", backgroundColor: "#1890ff" },
};

const renderSectionTitle = (title) => (
  <div style={styles.sectionTitle}>
    <span style={{ marginRight: 6 }}>{title}</span>
  </div>
);

function RefinateFiltersMapView({
  t,
  currency,
  programmatic,
  priceSliderMax,
  displaysMapMediaOwners,
  commonResolutions,
  typeOptions,
  subTypeOptions,
  localLocationType,
  localLocationSubType,
  localSizeType,
  localIsCircuit,
  resolutionSelect,
  customResolution,
  resolutionTolerance,
  enableAspectRatio,
  durationRange,
  priceMin,
  priceMax,
  selectedFormats,
  selectedMediaCompanies,
  onTypeChange,
  onSubTypeChange,
  onSizeChange,
  onCircuitChange,
  onResolutionSelect,
  onCustomResolution,
  onResolutionTolerance,
  onAspectRatioChange,
  onFormatChange,
  onDurationChange,
  onPriceChange,
  onMediaCompanyChange,
  onPriceSliderChange,
  onClear,
  onApply,
}) {
  const isSmall = localSizeType.includes("small");
  const isMedium = localSizeType.includes("medium");
  const isBig = localSizeType.includes("big");
  const isGiant = localSizeType.includes("giant");

  const currencySuffix = (
    <span style={styles.suffixText}>
      {currency ? currency.toLowerCase() : "usd"}
    </span>
  );
  const timeSuffix = (
    <span style={styles.suffixText}>
      {t("seconds") ? t("seconds").substring(0, 3) : "seg"}
    </span>
  );

  const isTraditional = programmatic === "traditional";

  const pMin = parseFloat(priceMin) || 0;
  const pMax = parseFloat(priceMax) || priceSliderMax;
  const sliderPriceValue = [pMin, Math.min(pMax, priceSliderMax)];

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={styles.mainGutter} style={{ marginBottom: styles.rowGap }}>
        <Col xs={24} md={12}>
          {renderSectionTitle(
            <React.Fragment>
              {t("Price range")}{" "}
              <span style={{ color: "#bfbfbf", fontWeight: 400 }}>
                ({isTraditional ? t("Per day") : "CPM"})
              </span>
            </React.Fragment>,
          )}
          <Row gutter={10}>
            <Col span={12}>
              <span style={styles.label}>{t("From")}</span>
              <Input
                placeholder="0"
                value={priceMin}
                onChange={(e) => onPriceChange("priceMin", e.target.value)}
                suffix={currencySuffix}
                style={styles.inputRadius}
              />
            </Col>
            <Col span={12}>
              <span style={styles.label}>{t("To")}</span>
              <Input
                placeholder={t("No limits applied")}
                value={priceMax}
                onChange={(e) => onPriceChange("priceMax", e.target.value)}
                suffix={currencySuffix}
                style={styles.inputRadius}
              />
            </Col>
          </Row>
          <div style={{ padding: "0 5px" }}>
            <Slider
              range
              min={0}
              max={priceSliderMax}
              value={sliderPriceValue}
              onChange={onPriceSliderChange}
              trackStyle={[styles.sliderTrack]}
              handleStyle={[styles.sliderHandle, styles.sliderHandle]}
            />
          </div>
        </Col>

        <Col xs={24} md={12}>
          {renderSectionTitle(t("Screens"))}
          <Row gutter={16}>
            <Col span={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{ ...styles.label, marginBottom: 0, marginRight: 5 }}
                >
                  {t("Type")}
                </span>
              </div>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={t("Select")}
                onChange={onTypeChange}
                value={[...localLocationType]}
                maxTagCount={1}
                dropdownStyle={{ borderRadius: "6px" }}
              >
                {typeOptions}
              </Select>
            </Col>
            <Col span={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{ ...styles.label, marginBottom: 0, marginRight: 5 }}
                >
                  {t("Subtype")}
                </span>
              </div>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={t("Select")}
                onChange={onSubTypeChange}
                value={[...localLocationSubType]}
                maxTagCount={1}
                dropdownStyle={{ borderRadius: "6px" }}
              >
                {subTypeOptions}
              </Select>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={styles.mainGutter} style={{ marginBottom: styles.rowGap }}>
        <Col xs={24} md={12}>
          {renderSectionTitle(t("Resolutions"))}
          <Select
            mode="tags"
            style={{ width: "100%", marginBottom: 10 }}
            placeholder={t("Select")}
            optionFilterProp="children"
            value={resolutionSelect}
            onChange={onResolutionSelect}
            dropdownStyle={{ borderRadius: "6px" }}
          >
            {commonResolutions.map((res) => (
              <Option key={`${res.w}x${res.h}`} value={`${res.w}x${res.h}`}>
                {res.w}x{res.h}
              </Option>
            ))}
          </Select>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{ fontSize: 12, marginRight: 10, whiteSpace: "nowrap" }}
              >
                {t("Other resolution")}
              </span>

              <div style={{ position: "relative", width: "100%" }}>
                <Input
                  placeholder="123x123, 456x456"
                  value={customResolution}
                  onChange={onCustomResolution}
                  style={{ ...styles.inputRadius }}
                />
                <Tooltip title={t("Resolution tooltip")}>
                  <Icon
                    type="info-circle"
                    style={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: Colors.textMuted,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 12, marginRight: 5 }}>
                  {t("Tolerance")}
                </span>
                <Tooltip title={t("Tolerance tooltip")}>
                  <InputNumber
                    min={0}
                    max={50}
                    value={resolutionTolerance}
                    onChange={onResolutionTolerance}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace("%", "")}
                    style={{ width: 140, marginLeft: 10 }}
                  />
                </Tooltip>
              </div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 12, marginRight: 5 }}>
                  {t("Aspect ratio")}
                </span>
                <Tooltip title={t("Aspect ratio tooltip")}>
                  <Checkbox
                    checked={enableAspectRatio}
                    onChange={onAspectRatioChange}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={24} md={12}>
          {renderSectionTitle(t("Ad format"))}
          <div style={{ marginTop: 8 }}>
            <Checkbox.Group
              options={[
                { label: t("Image"), value: "image" },
                { label: t("Video"), value: "video" },
                { label: t("HTML"), value: "html" },
              ]}
              value={selectedFormats}
              onChange={onFormatChange}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={styles.mainGutter}>
        <Col xs={24} md={12}>
          {renderSectionTitle(t("Size"))}
          <Row style={{ marginBottom: 20 }}>
            <Col span={6}>
              <Checkbox checked={isSmall} onChange={() => onSizeChange("small")}>
                {t("Small")}
              </Checkbox>
            </Col>
            <Col span={6}>
              <Checkbox
                checked={isMedium}
                onChange={() => onSizeChange("medium")}
              >
                {t("Medium")}
              </Checkbox>
            </Col>
            <Col span={6}>
              <Checkbox checked={isBig} onChange={() => onSizeChange("big")}>
                {t("Large")}
              </Checkbox>
            </Col>
            <Col span={6}>
              <Checkbox checked={isGiant} onChange={() => onSizeChange("giant")}>
                {t("Giant")}
              </Checkbox>
            </Col>
          </Row>

          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ fontWeight: 500, marginRight: 10 }}>
              {t("Circuit")}:
            </span>
            <Switch
              checked={localIsCircuit}
              onChange={onCircuitChange}
              size="small"
            />
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div style={{ marginBottom: 24 }}>
            {renderSectionTitle(t("Ad duration range"))}
            <Row gutter={16} style={{ marginBottom: 5 }}>
              <Col span={12}>
                <span style={styles.label}>{t("From")}</span>
                <Input
                  type="number"
                  value={durationRange[0]}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    onDurationChange([val, durationRange[1]]);
                  }}
                  suffix={timeSuffix}
                  style={styles.inputRadius}
                />
              </Col>
              <Col span={12}>
                <span style={styles.label}>{t("To")}</span>
                <Input
                  type="number"
                  value={durationRange[1]}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    onDurationChange([durationRange[0], val]);
                  }}
                  suffix={timeSuffix}
                  style={styles.inputRadius}
                />
              </Col>
            </Row>
            <Slider
              range
              min={5}
              max={50}
              value={[
                Math.max(5, Math.min(50, durationRange[0])),
                Math.max(5, Math.min(50, durationRange[1])),
              ]}
              onChange={onDurationChange}
              trackStyle={[styles.sliderTrack]}
              handleStyle={[styles.sliderHandle, styles.sliderHandle]}
            />
          </div>

          <div>
            {renderSectionTitle(t("Media owners"))}
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder={t("Search")}
              onChange={onMediaCompanyChange}
              value={selectedMediaCompanies}
              dropdownStyle={{ borderRadius: "6px" }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option.props.children)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {displaysMapMediaOwners &&
                displaysMapMediaOwners.map((owner) => (
                  <Option key={owner.id} value={owner.id}>
                    {owner.name}
                  </Option>
                ))}
            </Select>
          </div>
        </Col>
      </Row>

      <Row
        type="flex"
        justify="end"
        align="middle"
        style={{ marginTop: 40, gap: "10px" }}
      >
        <Button
          onClick={onClear}
          style={{
            borderRadius: "6px",
            border: `1px solid ${Colors.borderStrong}`,
            color: Colors.textSecondary,
          }}
        >
          {t("Clear filters")}
        </Button>
        <Button
          type="primary"
          onClick={onApply}
          style={{
            borderRadius: "6px",
            paddingLeft: 30,
            paddingRight: 30,
            fontWeight: 500,
            backgroundColor: "#1d7eba",
            borderColor: "#1d7eba",
          }}
        >
          {t("Apply")}
        </Button>
      </Row>
    </div>
  );
}

export default RefinateFiltersMapView;
